import { RecordData } from '../../../types';
import { RecordingEvent } from './measured-data-details.types';
import type { AvSyncVideoRange } from './services/av-sync.types';
import { NeckOrientation } from './services/neck-angle.service';

export interface CsvProcessingContext {
  recordData: RecordData;
  events: RecordingEvent[];
  pieChartData: number[];
  pieChartColor: string[];
  neckAngleScatterData: Array<any>;
  neckOrientationData: NeckOrientation[];
  addRegionWithMouseEvent(params: any): void;
  addMarker(time: number, color: string, label?: string, position?: string): any;
  setCurrentStartOffset(offset: number): void;
  setTimelineOffset(): void;
  setVsVideoRange(range: AvSyncVideoRange | null): void;
}

/**
 * CSV解析結果を受け取り、イベントマーカーやチャート更新を行う。
 */
export function processCsvResults(results: any, context: CsvProcessingContext): void {
  const { recordData } = context;
  if (!recordData) {
    return;
  }

  const backColor = '#e6ebeb';
  const foreColor = '#7abfe8';

  let startOffsetS = 0;
  let startOffsetMs = 0;
  let lastSwallowEndTime = 0;
  let mwstTry = 1;
  let tickTock = true;
  let vsVideoStartMs: number | null = null;
  let vsVideoEndMs: number | null = null;

  for (let i = 1; results.data.length > i; i++) {
    const row = results.data[i];
    if (!row || row.length === 0) {
      continue;
    }

    const label = row[0] as string;

    if (label === 'G') {
      if (recordData.data.recordType === 'RSST') {
        const startEvent = +parseFloat(row[1]).toFixed(4) - startOffsetMs;
        const timeGap = +(startEvent - lastSwallowEndTime).toFixed(4);
        context.pieChartData.push(timeGap);
        context.pieChartColor.push(backColor);

        const swalDuration = +parseFloat(row[2]).toFixed(4);
        context.pieChartData.push(swalDuration);
        context.pieChartColor.push(foreColor);

        lastSwallowEndTime = startEvent + swalDuration;
      }

      context.addRegionWithMouseEvent({
        id: context.events.length.toString(),
        start: parseFloat(row[1]) / 1000.0,
        end: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        drag: false,
        resize: false,
        color: 'rgba(0, 255, 0, 0.2)',
      });

      context.addMarker(
        (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0) / 2.0,
        'rgba(0, 255, 0, 1)',
      );

      context.events.push({
        type: 'swallow',
        startTime: parseFloat(row[1]) / 1000.0,
        endTime: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        duration: parseFloat(row[2]),
        mouseover_x: 0,
        mouseover_y: 0,
      });
    }

    if (label === 'OWT') {
      context.addRegionWithMouseEvent({
        id: context.events.length.toString(),
        start: parseFloat(row[1]) / 1000.0,
        end: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        drag: false,
        resize: false,
        showTooltip: false,
        color: 'rgba(255, 165, 0, 0.3)',
      });
      context.addMarker(
        (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0) / 2.0,
        'rgba(255, 165, 0, 1)',
      );
      context.events.push({
        type: 'other',
        startTime: parseFloat(row[1]) / 1000.0,
        endTime: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        duration: parseFloat(row[2]),
        mouseover_x: 0,
        mouseover_y: 0,
      });
    }

    if (label === 'CRS') {
      context.addRegionWithMouseEvent({
        id: context.events.length.toString(),
        start: parseFloat(row[1]) / 1000.0,
        end: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        drag: false,
        resize: false,
        showTooltip: false,
        color: 'rgba(255, 0, 0, 0.3)',
      });
      context.addMarker(
        (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0) / 2.0,
        'rgba(255, 0, 0, 1)',
      );
      context.events.push({
        type: 'cough',
        startTime: parseFloat(row[1]) / 1000.0,
        endTime: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        duration: parseFloat(row[2]),
        mouseover_x: 0,
        mouseover_y: 0,
      });
    }

    if (label === 'CWT') {
      context.addRegionWithMouseEvent({
        id: context.events.length.toString(),
        start: parseFloat(row[1]) / 1000.0,
        end: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        drag: false,
        resize: false,
        showTooltip: false,
        color: 'rgba(255, 165, 0, 0.3)',
      });
      context.addMarker(
        (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0) / 2.0,
        'rgba(255, 165, 0, 1)',
      );
      context.events.push({
        type: 'other',
        startTime: parseFloat(row[1]) / 1000.0,
        endTime: (parseFloat(row[1]) / 1000.0) + (parseFloat(row[2]) / 1000.0),
        duration: parseFloat(row[2]),
        mouseover_x: 0,
        mouseover_y: 0,
      });
    }

    if (label === 'CDS') {
      context.addMarker(parseFloat(row[1]) / 1000.0, '#00C2F1', 'CD(S)', 'bottom');
    }

    if (label === 'VSS') {
      const startMs = parseCsvNumber(row[1]);
      if (startMs != null) {
        vsVideoStartMs = startMs;
      }
    }

    if (label === 'VSE') {
      const startMs = parseCsvNumber(row[1]);
      const endMs = parseCsvNumber(row[2]);
      if (startMs != null && vsVideoStartMs == null) {
        vsVideoStartMs = startMs;
      }
      if (endMs != null) {
        vsVideoEndMs = endMs;
      }
    }

    if (label === 'RSS') {
      startOffsetS = parseFloat(row[1]) / 1000.0;
      startOffsetMs = parseFloat(row[1]);
      context.addMarker(startOffsetS, '#00C2F1', 'RSSTSTART', 'bottom');
    }

    if (label === 'RSE') {
      context.addMarker(parseFloat(row[1]) / 1000.0, '#00C2F1', 'RSSTEND', 'bottom');
    }

    if (label === 'FWT') {
      const attempt = row[2];
      context.addMarker(parseFloat(row[1]) / 1000.0, '#00C2F1', $localize`:@@details.labels.marker.challenge:挑戦 ${attempt}:attempt:`);
    }

    if (label === 'MWST_RESULT') {
      context.addMarker(parseFloat(row[1]) / 1000.0, '#00C2F1', row[3]);
    }

    if (label === 'MWST_FLOW') {
      context.addMarker(
        parseFloat(row[1]) / 1000.0,
        '#00C2F1',
        `F${mwstTry}(S)`
      );
      mwstTry++;
    }

    if (label.startsWith('GOST_')) {
      const state = label.charAt(5);
      const quantity = label.charAt(6);
      const toromi = label.charAt(7);

      context.addMarker(
        parseFloat(row[1]) / 1000.0,
        '#00C2F1',
        `S${quantity}${toromi === 'T' ? toromi : ''}(${state})`,
        tickTock ? 'top' : 'bottom'
      );

      if (state === 'E') {
        tickTock = !tickTock;
      }
    }

    if (label.startsWith('MWST_')) {
      const state = label.charAt(5);
      const attempt = label.charAt(6);
      let dry = label.charAt(7);
      if (dry === 'undefined') {
        dry = '';
      }

      context.addMarker(
        parseFloat(row[1]) / 1000.0,
        '#00C2F1',
        `A${attempt}${dry === 'D' ? dry : ''}(${state})`,
        tickTock ? 'top' : 'bottom'
      );

      if (state === 'E') {
        tickTock = !tickTock;
      }
    }

    if (label.startsWith('FOOD_TEST_')) {
      const state = label.charAt(10);
      const attempt = label.charAt(11);
      let dry = label.charAt(12);
      if (dry === 'undefined') {
        dry = '';
      }

      context.addMarker(
        parseFloat(row[1]) / 1000.0,
        '#00C2F1',
        `A${attempt}${dry === 'D' ? dry : ''}(${state})`,
        tickTock ? 'top' : 'bottom'
      );

      if (state === 'E') {
        tickTock = !tickTock;
      }
    }

    if ((row as Array<string>).includes('ABT')) {
      context.addMarker(parseFloat(row[1]) / 1000.0, '#00C2F1', 'ABORT');
    }

    if (recordData.data.recordType === 'RSST' && (results.data.length - 1) === i) {
      const remainingTime = +((recordData.data.wavDuration - startOffsetMs) - lastSwallowEndTime).toFixed(4);
      context.pieChartData.push(remainingTime);
      context.pieChartColor.push(backColor);
    }

    if ((row as Array<string>).includes('POS')) {
      const timeMs = parseFloat(row[1]);
      const pitch = parseFloat(row[2]);
      const roll = parseFloat(row[3]);
      const yawRaw = row[4] !== undefined ? parseFloat(row[4]) : 0;
      const yaw = Number.isNaN(yawRaw) ? 0 : yawRaw;
      if ([timeMs, pitch, roll].some((v) => Number.isNaN(v))) {
        continue;
      }
      context.neckAngleScatterData.push([roll, pitch, timeMs, yaw]);
      context.neckOrientationData.push({
        timeMs,
        roll,
        pitch,
        yaw,
      });
    }
  }

  const vsVideoRange = buildVsVideoRange(
    vsVideoStartMs,
    vsVideoEndMs,
  );
  context.setVsVideoRange(vsVideoRange);
  context.setCurrentStartOffset(startOffsetS);
  context.setTimelineOffset();
}

function parseCsvNumber(value: unknown): number | null {
  const n = typeof value === 'number'
    ? value
    : parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) ? n : null;
}

function buildVsVideoRange(
  startMs: number | null,
  endMs: number | null,
): AvSyncVideoRange | null {
  if (startMs == null) {
    return null;
  }
  return {
    waveStartSec: startMs / 1000.0,
    waveEndSec: endMs == null ? null : endMs / 1000.0,
  };
}
